import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Auth() {
  const { t, login, register } = useApp();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const rawNext = new URLSearchParams(window.location.search).get("next") || "";
  const nextPath = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/";
  const [f, setF] = useState({ name: "", email: "", password: "", terms: false, age: false });
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const [unverified, setUnverified] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setUnverified(false);
    try {
      if (mode === "login") { await login(f.email, f.password); toast.success("Welcome back"); nav(nextPath); }
      else { if (!f.terms) { toast.error(t("termsRequired")); setBusy(false); return; } if (!f.age) { toast.error(t("ageRequired")); setBusy(false); return; } if (nextPath !== "/") localStorage.setItem("gg_next", nextPath); await register(f.name, f.email, f.password, true, true); setSentTo(f.email); }
    } catch (err) {
      if (err.response?.status === 403) setUnverified(true);
      else toast.error(err.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const resend = async () => {
    await api.post("/auth/resend-verification", { email: sentTo || f.email });
    toast.success(t("verifySentAgain"));
  };
  const forgot = async () => {
    if (!f.email) { toast.error(t("email")); return; }
    await api.post("/auth/forgot-password", { email: f.email });
    toast.success(t("resetSent"));
  };

  if (sentTo) return (
    <div className="max-w-md mx-auto px-4 py-10">
      <div className="gg-card p-6 text-center" data-testid="verify-sent-card">
        <div className="eyebrow mb-3">{t("register")}</div>
        <h1 className="font-display uppercase text-3xl tracking-tighter">{t("checkInbox")}</h1>
        <p className="mt-4 text-sm">{t("verifySent", { email: sentTo })}</p>
        <button onClick={resend} className="gg-btn mt-6" data-testid="resend-verification-btn">{t("resendEmail")}</button>
        <button onClick={() => { setSentTo(null); setMode("login"); }} className="block w-full mt-3 underline eyebrow" data-testid="verify-sent-back">{t("backToLogin")}</button>
      </div>
    </div>
  );

  const googleLogin = () => {
    if (nextPath !== "/") sessionStorage.setItem("gg_next", nextPath);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-4 py-10">
      <div className="gg-card p-6">
        <h1 className="font-display uppercase text-3xl md:text-4xl tracking-tighter">{mode === "login" ? t("login") : t("register")}</h1>
        <p className="text-sm text-muted-foreground mt-2">{t("signInPitch")}</p>
        {unverified && (
          <div className="mt-4 p-3 border border-primary text-sm" data-testid="unverified-notice">
            {t("notVerified")}
            <button onClick={resend} className="block mt-2 underline eyebrow" data-testid="resend-verification-btn">{t("resendEmail")}</button>
          </div>
        )}
        <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
          {mode === "register" && (
            <><label className="eyebrow">{t("yourName")}</label>
            <input required data-testid="auth-name" className="gg-input" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></>
          )}
          <label className="eyebrow">{t("email")}</label>
          <input required type="email" data-testid="auth-email" className="gg-input" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} />
          <label className="eyebrow">{t("password")}</label>
          <input required type="password" minLength={mode === "register" ? 12 : 1} data-testid="auth-password" className="gg-input" value={f.password} onChange={e => setF({ ...f, password: e.target.value })} />
          <button disabled={busy} type="submit" data-testid="auth-submit" className="gg-btn-primary mt-2">{mode === "login" ? t("login") : t("createAccount")}</button>
          {mode === "register" && (
            <>
            <label className="flex items-start gap-2 text-xs text-muted-foreground min-h-[44px]" data-testid="auth-age-note">
              <input type="checkbox" required className="w-4 h-4 mt-0.5 shrink-0" checked={f.age} onChange={e => setF({ ...f, age: e.target.checked })} data-testid="auth-age" />
              <span>{t("ageConfirm")}</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-muted-foreground min-h-[44px]" data-testid="auth-terms-note">
              <input type="checkbox" required className="w-4 h-4 mt-0.5 shrink-0" checked={f.terms} onChange={e => setF({ ...f, terms: e.target.checked })} data-testid="auth-terms" />
              <span>{t("termsAgree1")} <Link to="/terms" className="underline" target="_blank">{t("terms")}</Link> {t("termsAgree2")} <Link to="/privacy" className="underline" target="_blank">{t("privacy")}</Link>.</span>
            </label>
            </>
          )}
          {mode === "login" && <button type="button" onClick={forgot} className="eyebrow underline self-start" data-testid="forgot-password-btn">{t("forgotPassword")}</button>}
        </form>
        <div className="my-4 flex items-center gap-2 text-xs eyebrow"><div className="flex-1 border-t border-foreground" />{t("or")}<div className="flex-1 border-t border-foreground" /></div>
        <button onClick={googleLogin} data-testid="auth-google" className="gg-btn w-full">
          <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.9 0 6.6 1.7 8.1 3.1l6-5.8C34.6 3.3 29.9 1 24 1 14.8 1 7.1 6.3 3.3 14.1l7 5.4C12.1 13.7 17.5 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.9-.4-4.2H24v8.2h12.7c-.3 2.1-1.7 5.2-4.9 7.3l7.5 5.8c4.5-4.2 7.2-10.4 7.2-17.1z"/><path fill="#FBBC05" d="M10.3 28.5c-.5-1.5-.8-3.1-.8-4.5s.3-3 .8-4.5l-7-5.4C1.5 17.4 0 20.6 0 24s1.5 6.6 3.3 9.9l7-5.4z"/><path fill="#34A853" d="M24 47c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2 1.4-4.7 2.4-8.4 2.4-6.5 0-11.9-4.2-13.7-10l-7 5.4C7.1 41.7 14.8 47 24 47z"/></svg>
          {t("continueWithGoogle")}
        </button>
        <div className="mt-6 text-sm text-center">
          {mode === "login" ? t("dontHaveAccount") : t("alreadyHaveAccount")}{" "}
          <button data-testid="auth-toggle" className="underline font-display uppercase text-xs tracking-wider" onClick={() => setMode(mode === "login" ? "register" : "login")}>
            {mode === "login" ? t("register") : t("login")}
          </button>
        </div>
      </div>
      <Link to="/" className="block text-center mt-4 eyebrow hover:underline">← {t("home")}</Link>
    </div>
  );
}
